import React, { useEffect, useRef } from 'react';
import { Typography } from '@mui/material';

import { CameraVideoPlaceholder } from 'components/camera/CameraVideoPlaceholder';
import { CameraVideoOverlay } from 'components/camera/CameraVideoOverlay';
import { useWebRTC } from 'hooks/useWebRTC';

import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './cameraVideoThumbnail.module.scss';

type CameraVideoThumbnailProps = {
  leaf: AppDeviceLeaf;
};

export const CameraVideoThumbnail = ({ leaf }: CameraVideoThumbnailProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  const { connect, remoteStream, rtcConnection, rtcConnectionState, rtcLoading, rtcError } = useWebRTC({
    leaf,
  });

  const isOverlayVisible = rtcConnectionState !== 'connected';
  const isPlaceholderVisible = !(remoteStream && rtcConnectionState === 'connected');

  // Set the video source when remoteStream changes
  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
    }
  }, [videoRef.current, remoteStream]);

  return (
    <div
      id={`videoThumbnailContainer-${leaf.id}`}
      className={styles.videoContainer}
      data-testid="component-camera-video-thumbnail"
      ref={videoPlayerRef}
    >
      {isOverlayVisible && (
        <CameraVideoOverlay
          cameraVideoOverlayType="preview"
          leaf={leaf}
          connect={connect}
          rtcConnection={rtcConnection}
          rtcLoading={rtcLoading}
          rtcError={rtcError}
        />
      )}
      {isPlaceholderVisible && (
        <CameraVideoPlaceholder
          leaf={leaf}
          cameraVideoPlaceholderType="preview"
          isBatteryTimeout={!!rtcConnection?.batteryTimeout}
        />
      )}
      <video
        className={isPlaceholderVisible ? styles.hidden : ''}
        ref={videoRef}
        id={`video-thumbnail-${leaf.id}`}
        autoPlay
        muted
        controls={false}
      />
      <div className={`${styles.videoOverlay}`}>
        <div className={styles.videoTitleContainer}>
          <Typography variant="h6" className={styles.videoTitle}>
            {leaf.name}
          </Typography>
        </div>
      </div>
    </div>
  );
};
